import { toJSON } from "@suse-o11y/to-json";

import type { BorgContentPart } from "../models/borg-types";

import type { PartGroup } from "./_message-utils";

export const isBlankText = (content: string | undefined): boolean => content == null || content.trim().length === 0;

const textComparisonKey = (part: Extract<BorgContentPart, { type: "text" }>): string =>
	`text:${part.part_id ?? ""}:${part.content ?? ""}:${part.severity ?? ""}`;

const thinkingComparisonKey = (part: Extract<BorgContentPart, { type: "thinking" }>): string =>
	`thinking:${part.part_id ?? ""}:${part.content ?? ""}`;

const toolCallComparisonKey = (part: Extract<BorgContentPart, { type: "tool_call" }>): string =>
	`tool_call:${part.part_id ?? ""}:${part.tool_use_id ?? ""}:${part.tool_name ?? ""}:${part.tool_status ?? ""}:${
		part.duration_ms ?? ""
	}:${toJSON(part.tool_args ?? {})}`;

const progressComparisonKey = (part: Extract<BorgContentPart, { type: "progress" }>): string =>
	`progress:${part.part_id ?? ""}:${part.content ?? ""}`;

const suggestionComparisonKey = (part: Extract<BorgContentPart, { type: "suggestion" }>): string =>
	`suggestion:${part.part_id ?? ""}:${part.content ?? ""}`;

const sourceLinkComparisonKey = (part: Extract<BorgContentPart, { type: "source_link" }>): string =>
	`source_link:${part.part_id ?? ""}:${part.label ?? ""}:${part.urn ?? ""}:${part.url ?? ""}:${part.link_type ?? ""}`;

const actionComparisonKey = (part: Extract<BorgContentPart, { type: "action" }>): string =>
	`action:${part.part_id ?? ""}:${part.label ?? ""}:${part.urn ?? ""}:${part.action_type ?? ""}:${part.query ?? ""}`;

const errorComparisonKey = (part: Extract<BorgContentPart, { type: "error" }>): string =>
	`error:${part.part_id ?? ""}:${part.content ?? ""}`;

export const partComparisonKey = (part: BorgContentPart): string => {
	switch (part.type) {
		case "text":
			return textComparisonKey(part);
		case "thinking":
			return thinkingComparisonKey(part);
		case "tool_call":
			return toolCallComparisonKey(part);
		case "progress":
			return progressComparisonKey(part);
		case "suggestion":
			return suggestionComparisonKey(part);
		case "source_link":
			return sourceLinkComparisonKey(part);
		case "action":
			return actionComparisonKey(part);
		case "error":
			return errorComparisonKey(part);
	}
};

export const normalizePartsForComparison = (parts: ReadonlyArray<BorgContentPart>): ReadonlyArray<BorgContentPart> =>
	parts.filter((part) => {
		if (part.type === "progress") {
			return false;
		}
		if (part.type === "text") {
			return !isBlankText(part.content);
		}
		return true;
	});

export const areEquivalentParts = (
	left: ReadonlyArray<BorgContentPart>,
	right: ReadonlyArray<BorgContentPart>,
): boolean => {
	const normalizedLeft = normalizePartsForComparison(left);
	const normalizedRight = normalizePartsForComparison(right);
	if (normalizedLeft.length !== normalizedRight.length) {
		return false;
	}
	return normalizedLeft.every((part, index) => partComparisonKey(part) === partComparisonKey(normalizedRight[index]));
};

const textPartKey = "text";
const thinkingPartKey = "thinking";
const progressPartKey = "progress";

export const getPartKey = (part: BorgContentPart): string => {
	if (part.part_id != null && part.part_id !== "") {
		return `part:${part.part_id}`;
	}
	switch (part.type) {
		case "text":
			return textPartKey;
		case "thinking":
			return thinkingPartKey;
		case "tool_call":
			return `tool_call:${part.tool_use_id ?? ""}:${part.tool_name ?? ""}`;
		case "progress":
			return progressPartKey;
		case "suggestion":
			return `suggestion:${part.content ?? ""}`;
		case "source_link":
			return `source_link:${part.label ?? ""}:${part.urn ?? ""}:${part.url ?? ""}:${part.link_type ?? ""}`;
		case "action":
			return `action:${part.label ?? ""}:${part.urn ?? ""}:${part.action_type ?? ""}`;
		case "error":
			return `error:${part.content ?? ""}`;
	}
};

export const isMessageActivelyThinking = (parts: ReadonlyArray<BorgContentPart>): boolean => {
	const lastVisiblePart = [...parts].reverse().find((part) => part.type !== "progress");
	return lastVisiblePart?.type === "thinking";
};

export const withStableGroupKeys = (
	groups: ReadonlyArray<PartGroup>,
): ReadonlyArray<{ group: PartGroup; key: string }> => {
	const seen: { [key: string]: number } = {};

	return groups.map((group) => {
		const base = `${group.key}:${group.items.map((part) => getPartKey(part)).join("|")}`;
		const count = (seen[base] ?? 0) + 1;
		seen[base] = count;
		return { group, key: `${base}:${count}` };
	});
};
